import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatSlideToggleChange, MatSnackBar } from '@angular/material';
// Import  TranslateService
import { TranslateService } from '@ngx-translate/core';
import { AppScopes } from 'app/core/authentication/models/scopes.enum';
import { AuthService } from 'app/core/authentication/services';
import { BrowserService } from 'app/core/browser/services/browser.service';
import { CustomFieldValueInterface, ProfileFilterValue, UserInterface } from 'app/core/user/models';
import { ErgoTeamMemberInterface } from 'app/core/user/models/ergo-team-member.model';
import { UserService } from 'app/core/user/services';
import { NotificationDialogService } from 'app/notifications/services/notification-dialog.service';
import { ErgoRoleInterface } from 'app/role/models/ergo-role.model';
import { RoleInterface } from 'app/role/models/role.model';
import { RoleService } from 'app/role/services/role-service';
import { FilterInterface, SiteFilterInterface } from 'app/site-filters/models';
import { SiteFilterService } from 'app/site-filters/services/site-filter.service';
import { SiteSettingType } from 'app/site-settings/enum/site-setting-type.enum';
import { CustomFieldInterface } from 'app/site-settings/models/custom-field.model';
import { SiteSettingInterface } from 'app/site-settings/models/site-setting.model';
import { SiteSettingsService } from 'app/site-settings/services/site-settings.service';
import { TaskDialogDataInterface } from 'app/task/models/task-dialog-data.model';
import { SSOStatusInterface } from "app/site-settings/models/active.model";
import { TaskDialogService } from 'app/task/services/task-dialog.service';
import { GlobalConstants } from 'app/shared/constants/globalconstants';
import { BehaviorSubject, combineLatest, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NameLinkOptions } from '../user-name-link/user-name-link.component';

import { PasswordResetComponent } from '../../../login/components/password-reset/password-reset.component';

const newUserTemplate: UserInterface = {
  id: -1,
  firstName: "",
  lastName: "",
  userName: "",
  primaryEmail: "",
  primaryPhone: "",
  gender: 3,
  roleId: undefined,
  userDisabled: false,
  ssoAccessOnly: false,
  profileFilterValues: [],
  customFieldValues: [],
};

@Component({
  selector: 'cz-user-profile-dialog',
  templateUrl: './user-profile-dialog.component.html',
  styleUrls: ['./user-profile-dialog.component.scss'],
})
export class UserProfileDialogComponent implements OnInit, OnDestroy {
  public loginUserInfo: UserInterface;
  public user: UserInterface;
  public siteFilters: FilterInterface[];
  public userRoleData: RoleInterface[];
  public ergoTeam: ErgoTeamMemberInterface[];
  public adminUsers: UserInterface[];
  public adminUsersFiltered$: BehaviorSubject<UserInterface[]>;
  public ergoRoles: ErgoRoleInterface[];
  public customFields: CustomFieldInterface[];
  // apparently passing a string directly in the template doesn't work....
  public customFieldsFloatState: string = 'always';

  public saveComplete: boolean = false;
  public loadComplete: boolean = false;

  public canEdit: boolean = false;
  public canChangeRole: boolean = false;
  public canEditErgoTeam: boolean = false;
  public isEmployee: boolean = false;
  public userNameChecked: boolean = true;
  public canSave: boolean = true;
  public addUser: boolean;
  public latestValue: string;

  public detailsForm: FormGroup;
  public filterForm: FormGroup;
  public customFieldForm: FormGroup;
  public userNameControl: FormControl;
  public ergoTeamMemberControl: FormControl;
  public ergoTeamRoleControl: FormControl;

  public ergoTeamBadgeConfig: NameLinkOptions = {
    showBadge: false,
    badgeOnRight: false,
    showLogout: false,
    showUserName: true,
    linkToProfile: true,
  };
  public resetPasswordButton: boolean;
  private scopeResults: {[id: string]: boolean};
  private loggedInUser: UserInterface;
  private isTaskAdmin: boolean;
  private userProfileEditSetting: SiteSettingInterface;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  // Inject TranslateService
  constructor(private _siteFiltersService: SiteFilterService,
              private _siteSettingsService: SiteSettingsService,
              private _formBuilder: FormBuilder,
              private _userService: UserService,
              private _roleService: RoleService,
              private _authService: AuthService,
              private _translateService: TranslateService,
              private _taskDialogService: TaskDialogService,
              private _notificationDialogService: NotificationDialogService,
              private _browserService: BrowserService,
              private _snackBar: MatSnackBar,
              private dialog: MatDialog,
              public _GlobalConstants: GlobalConstants,
              @Optional() public dialogRef: MatDialogRef<UserProfileDialogComponent>,
              @Optional() @Inject(MAT_DIALOG_DATA) public data: UserInterface) {
    this.userNameControl = new FormControl('', Validators.required);
    this.detailsForm = this._formBuilder.group({
      firstNameControl: [{ value: '' }, Validators.required],
      lastNameControl: [{ value: '' }, Validators.required],
      emailControl: [{ value: '' }, Validators.required],
      usernameControl: this.userNameControl,
      phoneControl: [{ value: '' }, Validators.required],
      genderControl: [{ value: '' }],
      userRoleControl: [{ value: '' }, Validators.required],
    });
    this.ergoTeamMemberControl = new FormControl('', Validators.required);
    this.ergoTeamRoleControl = new FormControl('', Validators.required);

    this.adminUsersFiltered$ = new BehaviorSubject<UserInterface[]>(undefined);
  }

  public ngOnInit(): void {
    if(!this.data) {
      this.addUser = true;
      this.user = newUserTemplate;
      this.loadComplete = true;
      this._userService.isUserNameValid()
      .pipe(takeUntil(this.destroy$))
      .subscribe((checkResult: { value: string, result: boolean }) => {
        this.detailsForm.updateValueAndValidity();
        if(checkResult.value !== this.latestValue) {
          this.userNameChecked = false;
          return;
        }
        this.userNameChecked = true;
        if(!checkResult.result) {
          this.userNameControl.setErrors({ invalid: true });
        }
      });
    } else {
      this.user = this.data;
    }
    combineLatest(
      this._authService.isLoggedInUserAuthorizedSet([
        AppScopes.PROFILE_EDIT,
        AppScopes.ERGO_TEAM_MANAGEMENT,
        AppScopes.UPDATE_USER_ROLE,
        AppScopes.CASE_MANAGEMENT_MASTER,
      ]),
      this._authService.getAuthenticatedUser(),
    ).pipe(takeUntil(this.destroy$))
    .subscribe((data: [{[id: string]: boolean}, UserInterface]) => {
      if(data[0]) {
        this.scopeResults = data[0];
      }

      if(data[1]) {
        this.loggedInUser = data[1];
      }


      if(this.scopeResults && this.loggedInUser) {

        this._userService.findOne(this.loggedInUser.id)
    .pipe(takeUntil(this.destroy$))
    .subscribe((authenticatedUser: UserInterface) => {
      this.loginUserInfo = authenticatedUser;
    });
        this.canEdit = this.scopeResults[AppScopes.PROFILE_EDIT];
        this.canEditErgoTeam = this.scopeResults[AppScopes.ERGO_TEAM_MANAGEMENT];
        this.canChangeRole = this.scopeResults[AppScopes.UPDATE_USER_ROLE];
        this.isTaskAdmin = this.scopeResults[AppScopes.CASE_MANAGEMENT_MASTER];
        this.isEmployee = this.loggedInUser.id === this.user.id;
        // Now all users able get Password-Update button except 'Administrator Administrator' user
        if (this.loginUserInfo.roleLabel === 'Administrator (master)' && this.user.id !== 116) {
          this.resetPasswordButton = true;
        } else {
          this.resetPasswordButton = false;
        }

        this.getUserDetailsData();
      }
    });

    this._siteSettingsService
      .loadUsersProps()
      .pipe(takeUntil(this.destroy$))
      .subscribe((ssoProps: SSOStatusInterface) => {
        if (!ssoProps) {
          return;
        }
        this._GlobalConstants.ssoActive = ssoProps.active;
        this._GlobalConstants.ssoEnabled = ssoProps.enabled;
        this._GlobalConstants.userSsoOnly = ssoProps.ssoOnly;
        if(!this.data)
          this.user.ssoAccessOnly = ssoProps.ssoOnly;
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
  /**
   * Save user data method
   * Handles both create new and update existing
   */
  public saveUser(): void {
    if(!this.detailsForm.valid) {
      // All this just so the form controls turn red if you try to save before you
      // have even attempted to fill out the form....
      for (const key in this.detailsForm.controls) {
        if(this.detailsForm.get(key).invalid) {
          this.detailsForm.get(key).markAsTouched();
          this.detailsForm.get(key).setErrors({ invalid: true });
        }
      }
      return;

    }

    this.user = {
      ...this.user,
      firstName: this.detailsForm.get('firstNameControl').value,
      lastName: this.detailsForm.get('lastNameControl').value,
      userName: this.detailsForm.get('usernameControl').value,
      primaryEmail: this.detailsForm.get('emailControl').value,
      primaryPhone: this.detailsForm.get('phoneControl').value,
      // not required so default if null
      gender: this.detailsForm.get('genderControl').value ?
        this.detailsForm.get('genderControl').value : 3,
      roleId: this.detailsForm.get('userRoleControl').value,
      profileFilterValues: [],
      customFieldValues: [],
    };

    this.siteFilters.forEach((filter: FilterInterface) => {
      const profileFilter: ProfileFilterValue = {
        filterId: filter.id.toString(),
        value: this.filterForm.get(filter.id.toString()).value.toString() ?
          this.filterForm.get(filter.id.toString()).value.toString() : '-1',
      };
      this.user.profileFilterValues.push(profileFilter);
    });

    this.customFields.forEach((field: CustomFieldInterface) => {
      const profileField: CustomFieldValueInterface = {
        id: field.id.toString(),
        value: this.customFieldForm.get(field.id.toString()).value.toString(),
      };
      this.user.customFieldValues.push(profileField);
    });
    if(this.user.id > 0) {
      this._userService.updateUser(this.user);
    } else {
      const subscriptionRef: Subscription = this._userService.createUser(this.user)
      .subscribe((createdUser: UserInterface) => {
        if(!createdUser || createdUser.id < 1) { return; }

        this.user = createdUser;
        this.getUserDetailsData();
        subscriptionRef.unsubscribe();
      });
    }
    this.saveComplete = true;

    // Static text to Language Variable updated
    const savedText: string = this.user.lastName + this._translateService.instant('$message_saved');
    this.showSnackBar(this._translateService.instant('$message_user') + this.user.firstName + ' ' + savedText);
    this.onNoClick();
  }

  public ergoTeamSelected(user: UserInterface): void {
    this.ergoTeamMemberControl.patchValue(user);
  }

  /**
   * Add Ergo Team Member to user
   * Can only be access with permissions and after user is created if new user
   */
  public addErgoTeamMember(): void {
    if(!this.ergoTeamMemberControl.valid || !this.ergoTeamRoleControl.valid) {
      return;
    }
    const selectedRole: ErgoRoleInterface = this.ergoTeamRoleControl.value as ErgoRoleInterface;

    const newTeamMember: ErgoTeamMemberInterface = {
      ergoRoleId: selectedRole.id,
      ergoRoleLabel: selectedRole.label,
      memberUser: this.ergoTeamMemberControl.value,
    };

    this._userService.addErgoTeamMember(this.user, newTeamMember);
   // Static text to Language Variable updated
    const staticText: string = this._translateService.instant('$message_new __team_member');
    const addedText: string = this._translateService.instant('$message_added');
    this.showSnackBar(staticText + newTeamMember.memberUser.firstName + ' ' + newTeamMember.memberUser.lastName + addedText);

    this.ergoTeamRoleControl.reset();
    this.ergoTeamMemberControl.reset();
  }
/**
 * Removes an ergo team member from the user's ergo team
 * @param memberToDelete The ergo team member object to be removed from the list
 */
  public deleteTeamMember(memberToDelete: ErgoTeamMemberInterface): void {
    this._userService.deleteErgoTeamMember(this.user, memberToDelete);
    // Static text to Language Variable updated
    const teamMText: string = this._translateService.instant('$message_team_member');
    const remoText: string = this._translateService.instant('$message_removed');
    this.showSnackBar(teamMText + memberToDelete.memberUser.firstName + ' ' + memberToDelete.memberUser.lastName + remoText);

  }
/**
 * Helper method that opens a create new task dialog with the current users information
 */
  public createTaskForUser(): void {
    const taskDialogData: TaskDialogDataInterface = {
      subject: this.user,
      assignedTo: this.loggedInUser,
      isTaskAdmin: this.isTaskAdmin,
    };

    this._taskDialogService.createNewTaskDialog(taskDialogData);
  }
/**
 * Helper method that opens a send notification dialog with the current users information
 */
  public sendNotificationToUser(): void {
    this._notificationDialogService.openNotificationDialog([this.user.id]);
  }
/**
 * Helper method that navigates to the user details page for the currently selected user
 */
  public goToUserDetails(): void {
    this._userService.setSelectedUser(this.user.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((returnedUserId: number) => {
        if (!returnedUserId) { return; }

        // close any open dialogs
        this.dialog.closeAll();

        // navigate to user details
        this._browserService.toUserDetails();
      });
  }

/**
 * Sends the user name to the api to verify that it is valid
 * Fires on keyup
 * @param event string value of the username input
 */
  public checkUserNameValue(event: any): void {
    this.userNameChecked = false;
    const value: string = event.target.value;
    this.latestValue = value;
    this._userService.checkUserName(value);
  }

  /**
   * Handles updates to the user access flag
   * @param event change event indicating selected or not selected
   */
  public updateDisabled(event: MatSlideToggleChange): void {
    this.user = {
      ...this.user,
      userDisabled: !event.checked,
    };
  }

  /**
   * Handles updates to the user access flag
   * @param event change event indicating selected or not selected
   */
  public updateSSOAccess(event: MatSlideToggleChange): void {
    this.user = {
      ...this.user,
      ssoAccessOnly: event.checked,
    };
  }

  public userSelectDisplay(user?: UserInterface): string | undefined {
    return user ? `${user.firstName} ${user.lastName}` : undefined;
  }


  /**
 * Reset Password dialog for admin
 */
  public openResetPassword(): void {
    if (!this.user.id) { return; }
    this.dialog.open(PasswordResetComponent, {
      height: '900px',
      width: '531px',
      panelClass: 'password-reset',
      data: { userId: this.user.id, type: 'admin' },
    });
  }

/**
 * Method fully hydrates the user and prepares form control list values via multiple api calls
 */
  private getUserDetailsData(): void {
    // Set up the subscriptions for the core user data
    combineLatest(
      this._userService.loadSingleUser(this.user.id),
      this._siteFiltersService.getFilterForProfile(),
      this._siteSettingsService.loadCustomFields(),
      this._roleService.loadAllRoles(),
      this._siteSettingsService.getSiteSetting(SiteSettingType.EmployeeCanEdit.toString()),
    )
    .pipe(takeUntil(this.destroy$))
    .subscribe((data: [UserInterface, SiteFilterInterface, CustomFieldInterface[], RoleInterface[], SiteSettingInterface]) => {
      // Each result set must be handled separately in order to deal with async issues....
      // update the user with new data if applicable
      if(data[0] && data[0].id > 0) {
        const userFromState: UserInterface = data[0];
        let userWithOnlyValues: UserInterface;
        userWithOnlyValues = {};
        for(const key of Object.keys(userFromState)) {
          if(userFromState[key] && userFromState[key] !== undefined) {
            userWithOnlyValues[key] = userFromState[key];
          }
        }
        this.user = {
          ...this.user,
          ...userWithOnlyValues,
        };
      }

      // build the form necessary for the user meta-data derived from the site filter values
      if(data[1]) {
        this.siteFilters = data[1].filters.filter((filter: FilterInterface) => {
          if(filter.items && filter.items.length > 0) {
            return filter;
          }
        });
        this.filterForm = this._formBuilder.group(this.generateSiteFilterForm(this.siteFilters, this.user));
      }

      // build the form necessary for the user meta-data derived from the custom field values
      if(data[2]) {
        this.customFields = data[2].filter((customField: CustomFieldInterface) => {
          if(customField.enabled) {
            return customField;
          }
        });
        this.customFieldForm = this._formBuilder.group(this.generateCustomFieldForm(this.customFields, this.user));
      }

      // Core user data form
      if(data[3]) {
        // set the role data
        this.userRoleData = data[3];
      }

      if(data[4]) {
        this.userProfileEditSetting = data[4];
      }

      if(this.user
         && this.userRoleData
         && this.userProfileEditSetting
         && this.siteFilters
         && this.customFields) {
        this.loadFormwithUser(this.user);

        if(this.canEdit) {
          this.filterForm.enable();
          this.customFieldForm.enable();
        } else if (this.userProfileEditSetting.value && this.isEmployee) {
          this.filterForm.enable();
          this.customFieldForm.enable();
        } else {
          this.filterForm.disable();
          this.customFieldForm.disable();
          this.canSave = false;
        }
        this.loadComplete = true;
      }
    });

    // subscriptions to get ergo team roles and ergo team users
    combineLatest(
      this._userService.findAllAdminUsers(),
      this._roleService.loadAllErgoRoles(),
      this._userService.getUserErgoTeam(this.user),
    )
    .pipe(takeUntil(this.destroy$))
    .subscribe((data: [UserInterface[], ErgoRoleInterface[], UserInterface]) => {
      if(data[0] && data[0].length > 0) {
        this.adminUsers = data[0].filter((adminUser: UserInterface) => {
          if(adminUser.id !== this.user.id) {
            return adminUser;
          }
        });
      }

      if(data[1] && data[1].length > 0) {
        this.ergoRoles = data[1];
      }

      if(data[2] && data[2].ergoTeam) {
        this.ergoTeam = data[2].ergoTeam;
      }

      if(this.adminUsers && this.ergoTeam) {
        this.adminUsersFiltered$.next(this.removeTeamFromAdminUsers(this.adminUsers, this.ergoTeam));
      }
    });
  }
/**
 * Updates the user details form with user data
 * @param detailedUser user information to update to the form controls
 */
  private loadFormwithUser(detailedUser: UserInterface): void {
    this.detailsForm.patchValue({
      firstNameControl: detailedUser.firstName ? detailedUser.firstName : '',
      lastNameControl: detailedUser.lastName ? detailedUser.lastName : '',
      emailControl: detailedUser.primaryEmail ? detailedUser.primaryEmail : '',
      usernameControl: detailedUser.userName ? detailedUser.userName : '',
      phoneControl: detailedUser.primaryPhone ? detailedUser.primaryPhone : '',
      genderControl: detailedUser.gender ? detailedUser.gender : '',
      userRoleControl: detailedUser.roleId ? detailedUser.roleId : '',
    });

    // We have a lot of rules about editing....
    if(this.canEdit) {
      this.detailsForm.enable();
      // handle the new user case
      if(this.canChangeRole || this.user.id < 1) {
        this.detailsForm.get('userRoleControl').enable();
      } else {
        this.detailsForm.get('userRoleControl').disable();
      }
      // new user case
      if(this.user.id < 0) {
        this.detailsForm.get('usernameControl').enable();
      } else {
        this.detailsForm.get('usernameControl').disable();
      }
    } else if (this.userProfileEditSetting.value && this.isEmployee) {
      this.detailsForm.disable();
      this.detailsForm.get('firstNameControl').enable();
      this.detailsForm.get('lastNameControl').enable();
      this.detailsForm.get('phoneControl').enable();
      this.detailsForm.get('genderControl').enable();
    } else {
      this.detailsForm.disable();
    }
  }
/**
 * creates a dynamic form based on currently activated custom fields and loads the fields with
 * relevant user data
 * @param fieldData custom field data from the site settings service
 * @param user currently loaded user
 */
  private generateCustomFieldForm(fieldData: CustomFieldInterface[], user: UserInterface): {} {
    const group: {[id: number]: FormControl} = {};
    const attemptMatch: boolean = user && user.customFieldValues ? true : false;

    if(!fieldData || fieldData.length === 0) { return group; }

    fieldData.forEach((customField: CustomFieldInterface) => {
      let initialValue: string = '';
      if(attemptMatch) {
        for(const userField of user.customFieldValues) {
          if(parseInt(userField.id, 10) === customField.id) {
            initialValue = userField.value;
            break;
          }
        }
      }

      group[customField.id] = new FormControl(initialValue);
    });

    return group;
  }
/**
 * creates a dynamic form based on the currently activated site filter fields and loads the fields
 * with relevant user data
 * @param filterData site filter data from the site filter service
 * @param user currently loaded user
 */
  private generateSiteFilterForm(filterData: FilterInterface[], user: UserInterface): {} {
    const group: {[id: number]: FormControl} = {};
    const attemptMatch: boolean = user && user.profileFilterValues ? true : false;

    if(!filterData || filterData.length === 0) { return group; }

    filterData.forEach((siteFilter: FilterInterface) => {
      let filterInitialValue: number = -1;

      if(attemptMatch) {
        for (const userFilter of user.profileFilterValues) {
          if(parseInt(userFilter.filterId, 10) === siteFilter.id) {
            filterInitialValue = parseInt(userFilter.value, 10);
            break;
          }
        }
      }

      group[siteFilter.id] = new FormControl(filterInitialValue);
    });

    return group;
  }
  /**
   * generic snack bar notifier to notify users of specific events
   * @param message message to display in snackbar
   */
  private showSnackBar(message: string): void {
    this._snackBar.open(message, undefined, {
      duration: 5000,
    });
  }

  /**
   * Adapted From https://stackoverflow.com/a/33356784/2174377
   */
  private removeTeamFromAdminUsers(list1: UserInterface[], list2: ErgoTeamMemberInterface[]): UserInterface[] {
    return list1.filter(
            ((set: Set<number>): any => (a: { id: number; }): any => !set.has(a.id))
            (new Set(list2.map((b: ErgoTeamMemberInterface) => b.memberUser.id))));
  }
  private onNoClick(): void {
    this.dialogRef.close();
  }
}
